import { useState, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import ApiService from "../../../data/ApiService";
import { useLoading } from "../../loadings/WidgetLoading";
import ConstantsTexts from "../../../constants/ConstantsTexts";

// Item mode types
const ITEM_MODES = {
  VIEW: "view",
  EDIT: "edit",
  DELETE: "delete",
};

// Field configuration for dynamic rendering
const FIELD_CONFIG = [
  { textKey: ConstantsTexts.CODE_31, fieldType: "workNeed" },
  { textKey: ConstantsTexts.CODE_123, fieldType: "organizationInCharge" },
  { textKey: ConstantsTexts.CODE_69, fieldType: "terminalStatus" },
  { textKey: ConstantsTexts.CODE_124, fieldType: "budgetStatus" },
  { textKey: ConstantsTexts.CODE_125, fieldType: "terminalOwnership" },
];

export const useWidgetAlertDialogUpdateModelFields = () => {
  const { showLoading, hideLoading } = useLoading();
  const userData = useSelector((state) => state.user.userData);

  // State for model options data
  const [modelOptionsData, setModelOptionsData] = useState(() => {
    const initialData = {};
    FIELD_CONFIG.forEach((field) => {
      initialData[field.fieldType] = [];
    });
    return initialData;
  });

  // State for selected field type
  const [selectedFieldType, setSelectedFieldType] = useState(FIELD_CONFIG[0]?.fieldType || "workNeed");
  // State for edited values
  const [editedValues, setEditedValues] = useState({});
  // State for create new field
  const [newFieldText, setNewFieldText] = useState("");
  // State for item modes (view, edit, delete)
  const [itemModes, setItemModes] = useState({});

  const showLoadingRef = useRef(showLoading);
  const hideLoadingRef = useRef(hideLoading);

  // Fetch model options values from API
  const fetchModelOptionsValues = useCallback(async () => {
    showLoadingRef.current();

    try {
      const response = await ApiService.getModelOptionsValues();

      if (response && response.data) {
        setModelOptionsData(response.data);
      }
    } catch (error) {
      console.error("Error fetching model options values:", error);
    } finally {
      hideLoadingRef.current();
    }
  }, []);

  // Fetch data when hook is initialized
  useEffect(() => {
    fetchModelOptionsValues();
  }, [fetchModelOptionsValues]);

  // Handle field type selection
  const handleFieldTypeSelect = useCallback((fieldType) => {
    setSelectedFieldType(fieldType);
    // Clear editing state when switching field types
    setEditedValues({});
    setItemModes({});
  }, []);

  // Handle entering edit mode for an item
  const handleEnterEditMode = useCallback((itemId, currentValue) => {
    setEditedValues((prev) => ({ ...prev, [itemId]: currentValue }));
    setItemModes((prev) => ({ ...prev, [itemId]: ITEM_MODES.EDIT }));
  }, []);

  // Handle canceling edit mode for an item
  const handleCancelEditMode = useCallback((itemId) => {
    setEditedValues((prev) => {
      const newValues = { ...prev };
      delete newValues[itemId];
      return newValues;
    });
    setItemModes((prev) => ({ ...prev, [itemId]: ITEM_MODES.VIEW }));
  }, []);

  // Handle saving edited value
  const handleSaveEdit = useCallback(
    async (itemId) => {
      const editedValue = editedValues[itemId];
      if (!editedValue) return;

      // Parse itemId to get field_name and value_serial
      const [fieldName, valueSerial] = itemId.split("_");

      // Get user information
      const userID = userData?.Username || "";
      const userName = userData?.UserAttributes?.find((attr) => attr.Name === "given_name")?.Value || "";

      const requestData = {
        field_name: fieldName,
        value_serial: parseInt(valueSerial),
        new_value: editedValue,
        userID: userID,
        userName: userName,
      };

      showLoadingRef.current();

      try {
        const response = await ApiService.editModelOptionValue(requestData);

        if (response.status === 200) {
          // Exit edit mode on success
          setItemModes((prev) => ({ ...prev, [itemId]: ITEM_MODES.VIEW }));

          // Refresh the data to show updated values
          await fetchModelOptionsValues();
        }
      } catch (error) {
        console.error("Error saving edited value:", error);
        // Keep edit mode active on error so user can retry
      } finally {
        hideLoadingRef.current();
      }
    },
    [editedValues, userData, fetchModelOptionsValues]
  );

  // Handle input change for edited values
  const handleInputChange = useCallback((itemId, value) => {
    setEditedValues((prev) => ({ ...prev, [itemId]: value }));
  }, []);

  // Handle new field input change
  const handleNewFieldInputChange = useCallback((value) => {
    setNewFieldText(value);
  }, []);

  // Handle adding new field
  const handleAddNewField = useCallback(async () => {
    if (!newFieldText.trim()) return;

    // Get user information
    const userID = userData?.Username || "";
    const userName = userData?.UserAttributes?.find((attr) => attr.Name === "given_name")?.Value || "";

    const requestData = {
      field_name: selectedFieldType,
      new_value: newFieldText.trim(),
      userID: userID,
      userName: userName,
    };

    showLoadingRef.current();

    try {
      const response = await ApiService.addModelOptionValue(requestData);

      if (response.status === 200) {
        // Clear the input
        setNewFieldText("");

        // Refresh the data to show the new field
        await fetchModelOptionsValues();
      }
    } catch (error) {
      console.error("Error adding new field:", error);
    } finally {
      hideLoadingRef.current();
    }
  }, [newFieldText, selectedFieldType, userData, fetchModelOptionsValues]);

  // Handle entering delete confirmation mode
  const handleEnterDeleteMode = useCallback((itemId) => {
    setItemModes((prev) => ({ ...prev, [itemId]: ITEM_MODES.DELETE }));
  }, []);

  // Handle canceling delete confirmation
  const handleCancelDeleteMode = useCallback((itemId) => {
    setItemModes((prev) => ({ ...prev, [itemId]: ITEM_MODES.EDIT }));
  }, []);

  // Handle confirming delete
  const handleConfirmDelete = useCallback(
    async (itemId) => {
      // Parse itemId to get field_name and value_serial
      const [fieldName, valueSerial] = itemId.split("_");

      // Get user information
      const userID = userData?.Username || "";
      const userName = userData?.UserAttributes?.find((attr) => attr.Name === "given_name")?.Value || "";

      const requestData = {
        field_name: fieldName,
        value_serial: parseInt(valueSerial),
        userID: userID,
        userName: userName,
      };

      showLoadingRef.current();

      try {
        const response = await ApiService.deleteModelOptionValue(requestData);

        if (response.status === 200) {
          // Exit delete mode and edit mode
          setItemModes((prev) => ({ ...prev, [itemId]: ITEM_MODES.VIEW }));

          // Refresh the data to show updated values
          await fetchModelOptionsValues();
        }
      } catch (error) {
        console.error("Error deleting field:", error);
      } finally {
        hideLoadingRef.current();
      }
    },
    [userData, fetchModelOptionsValues]
  );

  // Get item mode
  const getItemMode = useCallback(
    (itemId) => {
      return itemModes[itemId] || ITEM_MODES.VIEW;
    },
    [itemModes]
  );

  // Get edited value for an item
  const getEditedValue = useCallback(
    (itemId) => {
      return editedValues[itemId] || "";
    },
    [editedValues]
  );

  // Calculate total number of items for selected field type
  const getTotalItems = useCallback(() => {
    return modelOptionsData[selectedFieldType]?.length || 0;
  }, [modelOptionsData, selectedFieldType]);

  // Get options for selected field type
  const getSelectedFieldOptions = useCallback(() => {
    const fieldData = modelOptionsData[selectedFieldType] || [];

    const fieldTypeConfig = {
      workNeed: { displayNameKey: "workNeedName", serialKey: "workNeedSerial" },
      organizationInCharge: { displayNameKey: "organizationInChargeName", serialKey: "organizationInChargeSerial" },
      terminalStatus: { displayNameKey: "terminalStatusName", serialKey: "terminalStatusSerial" },
      budgetStatus: { displayNameKey: "budgetStatusName", serialKey: "budgetStatusSerial" },
      terminalOwnership: { displayNameKey: "terminalOwnershipName", serialKey: "terminalOwnershipSerial" },
    };

    const config = fieldTypeConfig[selectedFieldType];

    return fieldData.map((item) => ({
      ...item,
      type: selectedFieldType,
      displayName: item[config.displayNameKey],
      itemId: `${selectedFieldType}_${item[config.serialKey]}`,
    }));
  }, [modelOptionsData, selectedFieldType]);

  // Flatten all model options into a single array for rendering (for backward compatibility)
  const getAllOptions = useCallback(() => {
    return [
      ...modelOptionsData.workNeed.map((item) => ({ ...item, type: "workNeed", displayName: item.workNeedName })),
      ...modelOptionsData.organizationInCharge.map((item) => ({ ...item, type: "organizationInCharge", displayName: item.organizationInChargeName })),
      ...modelOptionsData.terminalStatus.map((item) => ({ ...item, type: "terminalStatus", displayName: item.terminalStatusName })),
      ...modelOptionsData.budgetStatus.map((item) => ({ ...item, type: "budgetStatus", displayName: item.budgetStatusName })),
      ...modelOptionsData.terminalOwnership.map((item) => ({ ...item, type: "terminalOwnership", displayName: item.terminalOwnershipName })),
    ];
  }, [modelOptionsData]);

  // Helper function to check if text already exists in selected field options
  const isTextAlreadyExists = useCallback(
    (text, excludeItemId = null) => {
      if (!text || !text.trim()) return false;
      const selectedOptions = getSelectedFieldOptions();
      return selectedOptions.some((option) => option.displayName.toLowerCase() === text.trim().toLowerCase() && option.itemId !== excludeItemId);
    },
    [getSelectedFieldOptions]
  );

  return {
    selectedFieldType,
    newFieldText,
    ITEM_MODES,
    FIELD_CONFIG,
    handleFieldTypeSelect,
    handleEnterEditMode,
    handleCancelEditMode,
    handleSaveEdit,
    handleInputChange,
    handleNewFieldInputChange,
    handleAddNewField,
    getItemMode,
    handleEnterDeleteMode,
    handleCancelDeleteMode,
    handleConfirmDelete,
    getEditedValue,
    fetchModelOptionsValues,
    getTotalItems,
    getSelectedFieldOptions,
    getAllOptions,
    isTextAlreadyExists,
  };
};

export default useWidgetAlertDialogUpdateModelFields;
